import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { useBucket } from "../../../bucket";
import type { BucketItemSnapshot } from "../../../bucket";
import { shownBucketTotal } from "../../../bucket/utils/shown-total";
import { useDisplayMoney } from "../../../../lib/currency";
import { KIT, PRIMARY, kitPanel, shortDate } from "./kit-tokens";

/** A line with no amount on it is a free one: restaurant tables cost nothing. */
export function isFreeLine(item: Pick<BucketItemSnapshot, "priceSnapshot">): boolean {
  return !(Number(item.priceSnapshot) > 0);
}

/** The bucket page in this language, keeping whatever the guest had in the URL. */
export function bucketHref(lang: string, search = ""): string {
  const q = search && !search.startsWith("?") ? `?${search}` : search;
  return `/${lang}/bucket${q}`;
}

/**
 * C27: the guest's trip, read from the same bucket the site uses.
 *
 * Restaurants say "Free". Everything in the bucket is an activity, paid on
 * the day, so there is one total and it is "due on the day". Stays are not in
 * the bucket (they are paid online through their own checkout), so there is
 * no stays line here to invent. The total is the sum of the shown, rounded,
 * converted lines, in one currency, never a mix.
 */
export function TripListCard({ lang }: { lang: string }) {
  const { t } = useTranslation();
  const { items, hydrated } = useBucket();
  const money = useDisplayMoney();
  // The query string is read after mount: the server has no window.
  const [search, setSearch] = useState("");
  useEffect(() => setSearch(window.location.search), []);

  if (!hydrated || items.length === 0) return null;

  const paid = items.filter((it) => !isFreeLine(it));
  const due = shownBucketTotal(paid, money.unit);
  const dueParts = money.parts(due);

  return (
    <div data-testid="kit-trip-list" className="flex flex-col p-3" style={kitPanel()}>
      <div className="mb-1.5 flex items-center gap-2">
        <ShoppingBag size={15} color={KIT.navy} aria-hidden />
        <span className="text-[13.5px] font-semibold" style={{ color: KIT.navy }}>
          {t("ai_concierge.kit.trip.title", { count: items.length })}
        </span>
      </div>
      <ul>
        {items.map((it) => {
          const free = isFreeLine(it);
          const p = free ? null : money.parts(money.unit(it.priceSnapshot));
          const guests = it.adults + it.children + it.infants;
          return (
            <li
              key={it.id}
              data-testid="kit-trip-line"
              className="flex items-center justify-between gap-3 border-t py-2 text-[12.5px]"
              style={{ borderColor: KIT.hairline }}
            >
              <span className="min-w-0">
                <span className="line-clamp-2 block break-words font-semibold" style={{ color: KIT.navy }}>
                  {it.title}
                </span>
                <span className="tabular-nums" style={{ color: KIT.muted }}>
                  {[
                    it.date ? shortDate(it.date, lang) : null,
                    it.time || null,
                    t("ai_concierge.kit.trip.guests", { count: guests }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span
                className="shrink-0 font-semibold tabular-nums"
                style={{ color: free ? KIT.green : KIT.navy }}
              >
                {p ? `${p.value} ${p.currency}` : t("ai_concierge.kit.trip.free")}
              </span>
            </li>
          );
        })}
      </ul>
      {due > 0 ? (
        <div
          data-testid="kit-trip-due"
          className="flex items-center justify-between border-t pt-2 text-[12.5px] tabular-nums"
          style={{ borderColor: KIT.hairline, color: KIT.navy }}
        >
          <span>{t("ai_concierge.kit.trip.due_on_day")}</span>
          <b>
            {dueParts.value} {dueParts.currency}
          </b>
        </div>
      ) : null}
      <a
        href={bucketHref(lang, search)}
        className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-full text-[13.5px] font-semibold"
        style={PRIMARY}
      >
        {t("ai_concierge.kit.trip.review")}
        <ArrowRight size={14} aria-hidden className="rtl:-scale-x-100" />
      </a>
    </div>
  );
}
